/*
 * The pinned messages of a conversation: the `chat.pinned` view of the details column, a tab
 * beside About and Files (X-A4 N1, N13). Newest pin first, each card the message's author, its
 * time, the first three lines of its body and who pinned it when; *Open* goes to the message's
 * permalink, which the timeline lands on and flashes, and *Unpin* takes it off the list for
 * everybody — a pin is the conversation's, not the pinner's.
 *
 * The list is read once and then kept live by the store (`pins.ts`): Bea's open list grows when
 * Anna pins, and a deleted message leaves it, without anybody reloading.
 */

import { useMemo, useState } from "react"
import { formatDate, formatDateTime, formatTime, isoDateTime } from "../../../core/i18n/format.ts"
import { Avatar } from "../../../core/people/Avatar.tsx"
import { MarkdownBody } from "../../../core/render/MarkdownBody.tsx"
import { RenderContextProvider } from "../../../core/render/context.ts"
import type { ConversationId } from "../../../core/store/model.ts"
import { useModel } from "../../../core/store/store.ts"
import { Alert, Button, ListState } from "../../../ui/index.ts"
import { t } from "../i18n/t.ts"
import { conversationPath, dmPath } from "../navigation.ts"
import "../render/chat.css"
import { buildRenderContext } from "../render/renderContext.ts"
import type { MessageView } from "../wire/views.ts"
import { setPinned } from "./api.ts"
import { goTo } from "./opening.ts"
import "./pinned.css"
import { reloadPins, usePins, type PinView } from "./pins.ts"

/** The registered `detailsViews` id of this view; a request names it, not the component. */
export const PINNED_VIEW = "chat.pinned"

/** The details group this view is a tab of, shared with the conversation's About and Files. */
export const CONVERSATION_DETAILS_GROUP = "chat.conversation"

/** Whether a details request is about a conversation at all — what every tab of the group asks. */
export function aboutAConversation(params: Record<string, string | number>): boolean {
	return params["conv"] !== undefined
}

/** The registered view: the details column hands every view the request's parameters. */
export function PinnedListView({ params }: { params: Record<string, string | number> }) {
	return <PinnedList conv={Number(params["conv"])} />
}

export function PinnedList({ conv }: { conv: ConversationId }) {
	const { status, pins, messages, hasMore } = usePins(conv)
	const users = useModel((model) => model.users)
	const groups = useModel((model) => model.groups)
	const conversations = useModel((model) => model.conversations)
	const meta = useModel((model) => model.conversations[conv]?.meta)
	const renderContext = useMemo(
		() => buildRenderContext({ users, groups, conversations }, goTo),
		[users, groups, conversations],
	)
	const [failed, setFailed] = useState("")

	// Where a card leads: the message's own address, in whichever shape this conversation has.
	const permalink = (seq: number): string => {
		const direct = meta?.kind === "dm" || meta?.kind === "groupDm"
		const base = direct ? dmPath(meta?.dmHandle ?? "") : conversationPath(meta?.slug ?? "")
		return `${base}/p/${String(seq)}`
	}
	const unpin = (pin: PinView) => {
		setFailed("")
		setPinned(pin.messageId, false).catch(() => setFailed(t("chat.pin.failed")))
	}

	if (status === "loading" || status === "idle") {
		return <ListState.Loading rows={2} label={t("chat.pinned.loading")} />
	}
	if (status === "failed") {
		return <ListState.Error message={t("chat.pinned.failed")} retry={() => void reloadPins(conv)} />
	}
	const cards = pins
		.map((pin) => ({ pin, message: messages[pin.messageId] }))
		.filter((entry): entry is { pin: PinView; message: MessageView } => entry.message !== undefined)
	// No door: a pin is made in the timeline, from a message's own menu, and a button here that jumped
	// there would land on whichever message happened to be on screen.
	if (cards.length === 0) return <ListState.Empty title={t("chat.pinned.empty")} />

	return (
		<RenderContextProvider value={renderContext}>
			{failed === "" ? null : <Alert>{failed}</Alert>}
			<ul className="tb-pinned" aria-label={t("chat.pinned.title")}>
				{cards.map(({ pin, message }) => (
					<li key={pin.messageId}>
						<PinnedCard
							pin={pin}
							message={message}
							authorName={users[message.authorId ?? 0]?.displayName ?? ""}
							authorAvatar={users[message.authorId ?? 0]?.avatarUrl}
							pinnerName={users[pin.byUserId]?.displayName ?? ""}
							onOpen={() => goTo(permalink(pin.seq))}
							onUnpin={() => unpin(pin)}
						/>
					</li>
				))}
			</ul>
			{/* The route answers one page and takes no cursor, so what the list owes the reader is the
			    truth that it is a page. */}
			{hasMore ? (
				<p className="tb-hint">{t("chat.pinned.capped", { count: cards.length })}</p>
			) : null}
		</RenderContextProvider>
	)
}

function PinnedCard({
	pin,
	message,
	authorName,
	authorAvatar,
	pinnerName,
	onOpen,
	onUnpin,
}: {
	pin: PinView
	message: MessageView
	authorName: string
	authorAvatar?: string | undefined
	pinnerName: string
	onOpen: () => void
	onUnpin: () => void
}) {
	return (
		<article
			className="tb-pinned__card"
			aria-label={t("chat.message.from", {
				author: authorName,
				time: formatDateTime(message.createdAt),
			})}
		>
			<div className="tb-pinned__head">
				<Avatar name={authorName} id={message.authorId} size="sm" url={authorAvatar} />
				<span className="tb-pinned__author">{authorName}</span>
				<time
					className="tb-pinned__time"
					dateTime={isoDateTime(message.createdAt)}
					title={formatDateTime(message.createdAt)}
				>
					{formatTime(message.createdAt)}
				</time>
			</div>
			<div className="tb-pinned__excerpt">
				<MarkdownBody render={message.render} source={message.markdown} />
			</div>
			{/* Two controls, one weight apart: going to the message is what a card is for, and taking the
			    pin off is the quieter of the two — it acts for everybody, and a pin is the conversation's
			    rather than the pinner's. */}
			<div className="tb-pinned__foot">
				<span>{t("chat.pinned.by", { name: pinnerName, date: formatDate(pin.at) })}</span>
				<span className="tb-pinned__actions">
					<Button variant="neutral" size="sm" label={t("chat.pinned.open")} onClick={onOpen} />
					<Button
						variant="ghost"
						quiet
						size="sm"
						label={t("chat.message.unpin")}
						onClick={onUnpin}
					/>
				</span>
			</div>
		</article>
	)
}
